import type {
  ServicesListResponse,
  ServicesUsageResponse,
} from "@/types/analytics-services";
import { at } from "../../utils/analyticsI18n";
import { SERVICES_COLORS } from "./constants";
import type {
  ServiceCategoryFilter,
  ServiceUsageSegment,
  TopServiceRow,
} from "./mockData";

const API_CATEGORY_TO_FILTER: Record<
  string,
  Exclude<ServiceCategoryFilter, "all">
> = {
  GUEST_SERVICES: "guestServices",
  ACTIVITIES: "activities",
  EXTERNAL_SERVICES: "externalServices",
  GUESTERIA_PARTNERS: "guesteriaPartners",
};

function toUiRevenue(valueMinor: number, divisor: number): number {
  if (!divisor) {
    return valueMinor;
  }
  return valueMinor / divisor;
}

function toImageSource(photoId: string | null | undefined): {
  imageUrl?: string;
  photoId?: string;
} {
  const value = photoId?.trim();

  if (!value) {
    return {};
  }

  if (/^https?:\/\//i.test(value)) {
    return { imageUrl: value };
  }

  return { photoId: value };
}

function mapApiCategoryToFilter(
  apiCategory: string | undefined,
  fallback: ServiceCategoryFilter,
): Exclude<ServiceCategoryFilter, "all"> {
  if (apiCategory && apiCategory in API_CATEGORY_TO_FILTER) {
    return API_CATEGORY_TO_FILTER[apiCategory];
  }
  return fallback === "all" ? "guestServices" : fallback;
}

export function mapServicesListToTopRows(
  response: ServicesListResponse | undefined,
  selectedCategory: ServiceCategoryFilter,
): TopServiceRow[] {
  if (!response) {
    return [];
  }

  return response.data.map((row, index) => ({
    id: row.service.id,
    rate: index + 1,
    name: row.service.name,
    ...toImageSource(row.service.photoId),
    category: mapApiCategoryToFilter(response.category, selectedCategory),
    price: row.service.price,
    bookings: row.bookings,
    guestsUsedPercent: Math.round(row.guestUsedPercent ?? 0),
    revenue: toUiRevenue(row.revenue, response.amountDivisor),
    availableFromTime: row.service.availableFromTime,
    availableToTime: row.service.availableToTime,
    availabilityDays: ["monday", "tuesday", "wednesday", "thursday", "friday"],
    outOfCatalog: row.outOfCatalog,
  }));
}

/** Guesteria Partners segment (and ALL) only — contract confirmed 2026-09-27. */
export function mapServicesListToOutOfCatalogSummary(
  response: ServicesListResponse | undefined,
): { count: number; revenue: number } | null {
  if (!response?.outOfCatalog) {
    return null;
  }

  return {
    count: response.outOfCatalog.count,
    revenue: toUiRevenue(response.outOfCatalog.revenue, response.amountDivisor),
  };
}

function mapCategorySegment(
  category: string,
  servicesUsed: number,
): ServiceUsageSegment | null {
  if (category === "GUEST_SERVICES") {
    return {
      key: "guestServices",
      name: at("categories.guestServices", "Guest Services"),
      value: servicesUsed,
      fill: SERVICES_COLORS.guestServices,
    };
  }
  if (category === "ACTIVITIES") {
    return {
      key: "activities",
      name: at("categories.activities", "Activities"),
      value: servicesUsed,
      fill: SERVICES_COLORS.activities,
    };
  }
  if (category === "EXTERNAL_SERVICES") {
    return {
      key: "externalServices",
      name: at("categories.externalServices", "External Services"),
      value: servicesUsed,
      fill: SERVICES_COLORS.externalServices,
    };
  }
  if (category === "GUESTERIA_PARTNERS") {
    return {
      key: "guesteriaPartners",
      name: at("categories.guesteriaPartners", "Guesteria Partners"),
      value: servicesUsed,
      fill: SERVICES_COLORS.guesteriaPartners,
    };
  }

  return null;
}

export function mapUsageToSegments(
  response: ServicesUsageResponse | undefined,
): ServiceUsageSegment[] {
  if (!response) {
    return [];
  }

  if (response.category === "ALL") {
    return response.byCategory
      .map((item) => mapCategorySegment(item.category, item.servicesUsed ?? 0))
      .filter((item): item is ServiceUsageSegment => item !== null);
  }

  // Selected category: group services by their category, not one segment per
  // service. Unknown categories stay separate segments under their own label.
  const totals = new Map<string, number>();
  for (const item of response.byService) {
    const label = item.categoryName?.trim() || item.service.name;
    totals.set(label, (totals.get(label) ?? 0) + (item.guestsUsed > 0 ? 1 : 0));
  }

  const palette = [
    SERVICES_COLORS.guesteriaPartners,
    SERVICES_COLORS.activities,
    SERVICES_COLORS.externalServices,
    SERVICES_COLORS.guestServices,
  ];

  return [...totals.entries()]
    .sort((a, b) => b[1] - a[1])
    .map(([label, value], index) => ({
      key: `category:${label}`,
      name: label,
      value,
      fill: palette[index % palette.length],
    }));
}
